import clsx from 'clsx';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { DecodePreview } from '@/api/workoutsService';
import { flattenPreview, summarize, DecodeRole } from './decodeUtils';

const RoleCell = ({ role }: { role: DecodeRole }) => {
  if (role === 'LOOP') return <span className="font-semibold text-brand-500">LOOP</span>;
  if (role === 'WORK') return <span className="font-semibold text-gray-900 dark:text-gray-100">WORK</span>;
  const suffix = role === 'REC R' ? 'R' : 'r';
  const isSection = role === 'REC R';
  return (
    <span className={clsx('font-semibold', isSection ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400')}>
      REC <span className="text-xs font-normal">{suffix}</span>
    </span>
  );
};

const DecodePreviewTable = ({
  preview,
  themeLabel,
  sportIdentifier,
}: {
  preview: DecodePreview;
  themeLabel?: string;
  sportIdentifier?: string;
}) => {
  const rows = flattenPreview(preview.sections, sportIdentifier);

  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50/60 dark:border-gray-800 dark:bg-gray-900/40">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pt-4">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">
          Décodage — {themeLabel ?? 'séance'}
        </h4>
        <span className="text-sm text-gray-400 dark:text-gray-500">aperçu avant enregistrement</span>
      </div>

      <div className="mt-3 border-t border-gray-200 dark:border-gray-800">
        <Table>
          <THead>
            <TR>
              <TH className="w-12">#</TH>
              <TH>Mesure</TH>
              <TH>Cible</TH>
              <TH>Rôle</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row, i) => {
              const isLoop = row.role === 'LOOP';
              const isSectionRec = row.role === 'REC R';
              return (
                <TR key={i} className={clsx(isSectionRec && 'bg-amber-50/70 dark:bg-amber-500/10')}>
                  <TD className="text-gray-400 dark:text-gray-500">{row.ellipsis ? '…' : row.stepNo}</TD>
                  <TD
                    className={clsx(
                      isLoop && 'font-medium text-brand-500',
                      isSectionRec && 'text-amber-600 dark:text-amber-400'
                    )}
                  >
                    {row.duration}
                  </TD>
                  <TD
                    className={clsx(
                      isLoop && 'text-brand-500',
                      isSectionRec && 'text-amber-600 dark:text-amber-400',
                      row.role === 'WORK' && 'text-gray-900 dark:text-gray-100'
                    )}
                  >
                    {row.target}
                  </TD>
                  <TD>
                    <RoleCell role={row.role} />
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>

      <p className="border-t border-gray-200 px-5 py-3 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {summarize(preview, sportIdentifier)}
      </p>

      {preview.warnings.length > 0 && (
        <div className="border-t border-amber-200 bg-amber-50/70 px-5 py-3 dark:border-amber-500/30 dark:bg-amber-500/10">
          {preview.warnings.map((w, i) => (
            <p key={i} className="text-sm text-amber-700 dark:text-amber-400">
              {w}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default DecodePreviewTable;
